import { useState, useEffect } from "react";
import { ToggleSwitch, TextInput, Select, Label, Button } from "flowbite-react";
import { useTranslation } from "react-i18next";
import { useGetPaginatedCategoriesQuery } from "@/api/ticketApi/category";
import { useGetPrioritiesQuery } from "@/api/ticketApi/selectInputApis";
import { HiFilter } from "react-icons/hi";
import PaginatedSelect from "@/components/PaginatedSelect";

interface SubcategoryFiltersProps {
  showInactive: boolean;
  onToggleInactive: (value: boolean) => void;
  onFilterChange: (filters: any) => void;
}

export default function SubcategoryFilters({
  showInactive,
  onToggleInactive,
  onFilterChange,
}: SubcategoryFiltersProps) {
  const { t } = useTranslation(["subcategory", "common"]);
  const [subcategoryName, setSubcategoryName] = useState("");
  const [categoryFilter, setCategoryFilter] = useState<{ label: string; value: string } | null>(null);
  const [priorityId, setPriorityId] = useState("");
  const [debouncedName, setDebouncedName] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  
  const [categoryPage, setCategoryPage] = useState(1);
  const [categorySearch, setCategorySearch] = useState("");

  const { data: categoriesData, isFetching: isCategoriesFetching } = useGetPaginatedCategoriesQuery({
    pagination: { page: categoryPage, pageSize: 15 },
    filters: { "category_name[like]": categorySearch }
  });
  
  const { data: prioritiesData } = useGetPrioritiesQuery();

  const categoryOptions = (categoriesData?.items || []).map((c: any) => ({
    label: c.category_name,
    value: String(c.category_id)
  }));

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedName(subcategoryName);
    }, 500);
    return () => clearTimeout(handler);
  }, [subcategoryName]);

  useEffect(() => {
    const filters: any = {};
    if (debouncedName) filters["subcategory_name[like]"] = debouncedName;
    if (categoryFilter) filters.category_id = parseInt(categoryFilter.value);
    if (priorityId) filters.priority_id = parseInt(priorityId);

    onFilterChange(filters);
  }, [debouncedName, categoryFilter, priorityId]);

  const handleClearFilters = () => {
    setSubcategoryName("");
    setCategoryFilter(null);
    setCategorySearch("");
    setCategoryPage(1);
    setPriorityId("");
    onToggleInactive(false);
  };

  const handleToggleFilters = () => {
    if (showFilters) {
      handleClearFilters();
    }
    setShowFilters(!showFilters);
  };

  return (
    <>
      <div className="mb-6 flex justify-start">
        <Button color="light" onClick={handleToggleFilters}>
          <HiFilter className="mr-2 h-5 w-5" />
          {showFilters ? t("common:hideFilters") : t("common:showFilters")}
        </Button>
      </div>

      {showFilters && (
        <div className="mb-6 rounded-lg bg-white p-4 shadow dark:bg-gray-800">
          <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
            {t("common:filters")}
          </h3>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
            <div>
              <Label htmlFor="subcategory-search" className="mb-2 block">
                {t("subcategory:subcategory_name")}
              </Label>
              <TextInput
                id="subcategory-search"
                className="mt-4"
                placeholder={t("subcategory:searchSubcategory")}
                value={subcategoryName}
                onChange={(e) => setSubcategoryName(e.target.value)}
              />
            </div>

            <div>
              <Label htmlFor="category-filter" className="mb-2 block">
                {t("subcategory:category")}
              </Label>
              <PaginatedSelect
                id="category-filter"
                value={categoryFilter}
                onChange={(val) => setCategoryFilter(val as { label: string; value: string } | null)}
                options={categoryOptions}
                onInputChange={(val) => { setCategorySearch(val); setCategoryPage(1); }}
                isLoading={isCategoriesFetching}
                placeholder={t("subcategory:allCategories")}
                page={categoryPage}
                totalPages={categoriesData?.pagination?.totalPages || 1}
                onPageChange={setCategoryPage}
                isClearable
                menuMaxHeight={350}
              />
            </div>

            <div>
              <Label htmlFor="priority-filter" className="mb-2 block">
                {t("subcategory:priority_label")}
              </Label>
              <Select
                id="priority-filter"
                value={priorityId}
                className="mt-4"
                onChange={(e) => setPriorityId(e.target.value)}
              >
                <option value="">{t("subcategory:allPriorities")}</option>
                {prioritiesData?.items?.map((p: any) => (
                  <option key={p.priority_id} value={p.priority_id}>
                    {p.priority_name}
                  </option>
                ))}
              </Select>
            </div>

            <div className="flex flex-col justify-end gap-4 pb-2">
              <ToggleSwitch
                checked={showInactive}
                label={t("subcategory:show_inactive")}
                onChange={onToggleInactive}
              />
              <Button color="light" size="sm" onClick={handleClearFilters}>
                {t("common:reset")}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
